import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { Fragment, useEffect, useMemo, useState } from 'react';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { formatDate, formatDateCMProgressBar } from '/@/utils';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import useIsMobile from '/@/utils/hooks/is-mobile';
import Title from '/@/v3/components/title';
import { BASE_STEPS, mergeStepsAndStages } from './assets/BASE_STEPS';
import ProgressBarDesktop from './desktop';
import ProgressBarMobile from './mobile';
import styles from './styles.module.css';

const ProgressBar = ({ cropMonitoring, showCropProtection, technology, showExpectedFloweringDate, showAlert }) => {
    const isMobile = useIsMobile();
    const [stages, setStages] = useState([]);
    const [activeStep, setActiveStep] = useState();

    const steps = useMemo(() => {
        const allSteps = isMobile ? BASE_STEPS : BASE_STEPS.filter((s) => !s.empty);
        const lastStage = stages[stages.length - 1];
        if (lastStage && ['flowering', 'maturity', 'harvested'].includes(lastStage.phenology)) {
            const leafStages = stages.filter(({ vegetativeStage }) => vegetativeStage !== null);
            const lastLeaft = leafStages[leafStages.length - 1]?.vegetativeStage;

            return allSteps.filter(
                ({ vegetativeStage }) => vegetativeStage === undefined || vegetativeStage <= lastLeaft
            );
        }

        return allSteps;
    }, [isMobile, stages]);

    const cropData = getCropMonitoringData(cropMonitoring);

    useEffect(() => {
        setStages(cropData?.stages);
    }, [cropData?.stages]);

    useEffect(() => {
        setActiveStep(getActiveStep());
    }, [stages]);

    useEffect(() => {
        updateDateSteps(steps);
    }, [steps]);

    const updateDateSteps = (allSteps) => mergeStepsAndStages(allSteps, stages, cropMonitoring.plantingDate);

    const getActiveStep = () => {
        let activeStep = 0;
        let lastStageDate = formatDate(new Date(cropMonitoring.plantingDate));
        const today = formatDate(new Date());
        const lastStage = stages[stages.length - 1];
        if (stages.length > 0) {
            lastStageDate = formatDate(new Date(lastStage.date));
            activeStep = steps.findIndex(
                (step) =>
                    (step.vegetativeStage === lastStage.vegetativeStage || step.phenology === lastStage.phenology) &&
                    !step.empty
            );
        }

        if (today !== lastStageDate || (lastStage && lastStage.phenology === 'harvested')) {
            return activeStep + 1;
        }
        return activeStep;
    };

    const getClassStep = (step, index, activeStep) => {
        if (!cropMonitoring?.id) return;
        if (activeStep > index) {
            return step.stage;
        }
    };

    const activeClassStep = (step, index, activeStep) => {
        if (activeStep > index + 1) {
            return step.stage;
        }
    };

    const isActiveStep = (index, activeStep) => {
        if (index + 1 > activeStep) {
            return true;
        }
        return false;
    };

    const isMobileActiveStep = (index, activeStep) => {
        if (index + 1 === activeStep) {
            return true;
        }
        return false;
    };

    const getDate = (step, index) => {
        if (step.state === 'R' && Boolean(!step.date) && !isMobile && Boolean(cropMonitoring.expectedFloweringDate)) {
            let expected = new Date(cropMonitoring.expectedFloweringDate);
            expected = formatDateCMProgressBar(formatDate(expected));
            return (
                <Title className={styles.criticDate}>
                    {expected}
                    <Tooltip title={_('Fecha de floración estimada')} arrow>
                        <InfoIcon />
                    </Tooltip>
                </Title>
            );
        }

        let today = new Date();
        today = formatDateCMProgressBar(formatDate(today));
        if (!step.date) return '';
        let date = new Date(step.date);
        date = formatDateCMProgressBar(formatDate(date));
        if (activeStep > index) {
            return date;
        }
        return '';
    };

    const getDataStep = (index, activeStep) => {
        if (isMobile && activeStep > index) {
            return '✓';
        }
        if (!isMobile && activeStep > index) {
            return '✓';
        }
        // alt 255 or broke compomnent
        return ' ';
    };

    const expectedFloweringDate = new Date(cropMonitoring.expectedFloweringDate);

    if (!isMobile || settings.app.theme === 'expertobayer') {
        return (
            <Fragment>
                <ProgressBarDesktop
                    showCropProtection={showCropProtection}
                    steps={updateDateSteps(steps)}
                    activeStep={cropMonitoring?.id ? activeStep : undefined}
                    isActiveStep={isActiveStep}
                    getClassStep={getClassStep}
                    getDate={getDate}
                    getDataStep={getDataStep}
                    technology={technology}
                    activeClassStep={activeClassStep}
                    showExpectedFloweringDate={showExpectedFloweringDate}
                />
            </Fragment>
        );
    }

    return (
        <ProgressBarMobile
            cropMonitoring={cropMonitoring}
            showCropProtection={showCropProtection}
            steps={updateDateSteps(steps)}
            activeStep={activeStep}
            isActiveStep={isMobileActiveStep}
            getClassStep={getClassStep}
            getDate={getDate}
            getDataStep={getDataStep}
            technology={technology}
            expectedFloweringDate={expectedFloweringDate}
            showAlert={showAlert}
        />
    );
};

export default ProgressBar;
